import { useEffect, useState } from 'react';
import styles from './Table.module.css';

export interface RoundClockProps {
  readonly startedAt: number;
  readonly revealedAt: number | null;
}

function format(ms: number): string {
  const total = Math.floor(ms / 1_000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * How long the round has been open. Owns its own ticking state so a second
 * passing re-renders four digits rather than every seat at the table.
 *
 * Both timestamps come from the server while `Date.now()` is local, so a client
 * with a badly skewed clock reads a wrong duration. Clamped at zero, which keeps
 * the worst case merely unhelpful rather than nonsense; this is informational and
 * not worth a clock-sync handshake on the wire.
 */
export function RoundClock({ startedAt, revealedAt }: RoundClockProps) {
  const live = revealedAt === null;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!live) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [live, startedAt]);

  const elapsed = Math.max(0, (revealedAt ?? now) - startedAt);

  return (
    <span className={styles.clock} aria-label="Time on this round">
      {format(elapsed)}
    </span>
  );
}
